import React, { useEffect, useState } from 'react';
import { Col, Form, FormInstance, Grid, Message, Row, Schema } from "rsuite";
import { AreaTypeStoryChapterTask, UpsertAreaTypeStoryChapterTask } from '@/models/story-chapter-task.model';
import Textarea from '@/components/Textarea';
import { getBoundingBoxCenter, getPolygonPathsFromGeoJson } from '@/utils/map';
import { LatLng } from '@/models/core.model';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { hk_lat_lng } from '@/constants/map';

const modelSchema = Schema.Model<UpsertAreaTypeStoryChapterTask>({
  jsonArea: Schema.Types.StringType().isRequired('This field is required.'),
});
const emptyModel: UpsertAreaTypeStoryChapterTask = {
  jsonArea: '',
};

interface AreaTypeTaskFormFormProps{
  formObject?: AreaTypeStoryChapterTask;
  onFormChange: (formValue: any) => void;
  formRef: React.Ref<FormInstance<Record<string, any>>>;
  loading: number;
}

const AreaTypeTaskForm: React.FC<AreaTypeTaskFormFormProps> = ({ formObject, onFormChange, formRef, loading }) => {
  const [formValue, setFormValue] = useState<UpsertAreaTypeStoryChapterTask>(emptyModel);
  const [polygonPaths, setPolygonPaths] = useState<LatLng[]>([]);
  const [mapCenter, setMapCenter] = useState<LatLng|undefined>(hk_lat_lng);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });

  useEffect(() => {
    if (formObject) {
      setFormValue(formObject);
      updatePolygon(formObject.jsonArea ?? '');
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  useEffect(() => {
    onFormChange(formValue);
  }, [formValue]);

  const updatePolygon = (geoJson: string) => {
    const polygons = getPolygonPathsFromGeoJson(geoJson);
    if(polygons && polygons.length > 0) {
      setPolygonPaths(polygons);
      setMapCenter(getBoundingBoxCenter(polygons[0]));
      return true;
    } else { 
      setPolygonPaths([]);
      return false;
    }
  };

  const handleJsonChangeChange = (geoJson: string) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      jsonArea: geoJson
    }));
    updatePolygon(geoJson);
  };
    
  return (
    <Form name='upsert-area-task-type' fluid
        ref={formRef}
        formValue={formValue}
        model={modelSchema}
        disabled={!!loading}
    >    
      <Grid w='100%' fluid>
        <Row>
          <Col span={{xs:24}}>
                <Form.Group>
                  <Form.Label>Json Area</Form.Label>
                  <Form.Control 
                    name="jsonArea" 
                    value={formValue.jsonArea ?? ''} 
                    accepter={Textarea} 
                    rows={6}
                    onChange={handleJsonChangeChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Area On Map</Form.Label>
                    {isLoaded ? <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '400px', borderRadius: 10 }}
                      center={mapCenter}
                      zoom={13}
                    >
                      <Polygon
                        paths={polygonPaths}
                        options={{
                          fillColor: '#2196f3',
                          fillOpacity: 0.4,
                          strokeColor: '#2196f3',
                          strokeOpacity: 1,
                          strokeWeight: 2
                        }}
                      />
                    </GoogleMap> : '' }                
                </Form.Group>                
          </Col>
        </Row>
        <Row>
          <Message showIcon type="info">
            User should run within a area. Amount: distance inside area.
          </Message>
        </Row>         
      </Grid>
    </Form>
  );
};

export default AreaTypeTaskForm;